import React, { useState, useEffect } from 'react';
import {
  Table, Button, Badge, Modal, Form, Row, Col, Card, Alert
} from 'react-bootstrap';
import { FaPlus, FaEdit, FaDownload, FaEye } from 'react-icons/fa';
import axios from 'axios';

const CCPADsarManagementTab = ({ userData, onScoreUpdate, showNotification, region, refreshActivities,logToAccessLogs }) => {
  const [requests, setRequests] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [viewingRequest, setViewingRequest] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, overdue: 0 });
// Add these after line 8 (after stats state)
const [showSubmitModal, setShowSubmitModal] = useState(false);
const [newRequest, setNewRequest] = useState({
  type: 'Right to Know',
  consumerEmail: '',
  firstName: '',
  lastName: '',
  details: '',
  verificationMethod: 'Email',
  phoneNumber: '',
  address: ''
});
const [submissionSuccess, setSubmissionSuccess] = useState(false);

  const headers = {
    Region: region,
    UserId: userData?.['cognito:username'] 
  };
// Add after line 12 (after headers)
const REQUEST_TYPES = [
  'Right to Know',
  'Right to Delete', 
  'Opt-Out of Sale',
  'Right to Correct',
  'Right to Limit Use of Sensitive Information',
  'Right to Non-Discrimination'
];

const VERIFICATION_METHODS = [
  'Email',
  'Phone',
  'Mail',
  'Government ID',
  'Account Information'
];

  useEffect(() => {
  if (userData?.['cognito:username']) {
    fetchRequests();
  }
}, [userData]);

  const fetchRequests = async () => {
    try {
      const res = await axios.get('https://4mdq06ir16.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/consumer-requests/list', { headers });
      setRequests(res.data);
      
      // Calculate stats
      const total = res.data.length;
      const pending = res.data.filter(r => r.status === 'Pending').length;
      const completed = res.data.filter(r => r.status === 'Completed').length;
      const overdue = res.data.filter(r => {
        const dueDate = new Date(r.dueDate);
        const today = new Date();
        return r.status !== 'Completed' && dueDate < today;
      }).length;

      setStats({ total, pending, completed, overdue });

      // Calculate compliance score (CCPA has 45-day response requirement)
      let score = 0;
      if (total > 0) {
        const onTime = res.data.filter(r => {
          const dueDate = new Date(r.dueDate);
          const completedDate = r.completedDate ? new Date(r.completedDate) : null;
          return r.status === 'Completed' && completedDate && completedDate <= dueDate;
        }).length;
        
        const responseRate = total > 0 ? (completed / total) * 100 : 0;
        const timelinessRate = completed > 0 ? (onTime / completed) * 100 : 0;
        const overdueRate = total > 0 ? (overdue / total) * 100 : 0;
        
        score = (responseRate * 0.5) + (timelinessRate * 0.3) - (overdueRate * 0.2);
        score = Math.max(0, Math.min(100, score));
      }
if (onScoreUpdate) {
  onScoreUpdate(Math.round(score));
}
    } catch (error) {
      console.error('Error fetching consumer requests:', error);
    }
  };

  const handleStatusUpdate = async (requestId, newStatus) => {

    try {
      const currentRequest = requests.find(r => r.requestId === requestId);
      await axios.put(`https://5uqra705x8.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/consumer-requests/update/${requestId}`, 
        { status: newStatus, completedDate: newStatus === 'Completed' ? new Date().toISOString() : null }, 
        { headers }
      );
      
      await logActivity(`Updated CCPA consumer request ${requestId} to ${newStatus}`, "primary");
      logToAccessLogs?.({
        action:      newStatus === 'Completed' ? 'DPAR Completed' : 'DPAR Updated',
        severity:    newStatus === 'Completed' ? 'success' : 'info',
        description: `CCPA ${currentRequest?.type} request for ${currentRequest?.consumerEmail} → ${newStatus}`,});
      showNotification(`Consumer request updated to ${newStatus}!`);
      fetchRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      showNotification('Error updating request', 'danger');
    }
  };

  const exportResponse = async (requestId) => {
    try {
      const res = await axios.get(`https://z8g6w6ovvg.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/consumer-requests/export/${requestId}`, 
        { headers, responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CCPA_Response_${requestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      await logActivity(`Exported CCPA response for request ${requestId}`, "info");
    } catch (error) {
      console.error('Error exporting response:', error);
      showNotification('Error exporting response', 'danger');
    }
  };

 const logActivity = async (message, badge = "info") => {
 
  
  try {
    // ✅ Map frontend fields to backend entity fields
    const payload = {
      action: badge,                    // badge → action
      module: "DSAR",              // Add module field
      description: message                  // message → details
      // userId and timestamp set by backend
    };
    
    
    
    const response = await axios.post(
      "https://wjioewyrif.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/activities/create",  // ✅ Correct endpoint
      payload,     
      { headers }  // ✅ Send headers properly
    );
    
    
    if (refreshActivities) {
        await refreshActivities();
      }


  } catch (err) {
    console.error('❌ Error logging inventory activity:', err);
    
    if (err.response) {
      console.error('🔥 Server Error Details:');
      console.error('Status:', err.response.status);
      console.error('Data:', err.response.data);
    }
    
    console.warn("Failed to log activity:", err.message);
  }
};

  // Add after logActivity function
const handleSubmitRequest = async () => {
  try {
    const requestDate = new Date();
    const dueDate = new Date(requestDate.getTime() + (45 * 24 * 60 * 60 * 1000)); // 45 days later
    
    const payload = {
      ...newRequest,
      requestDate: requestDate.toISOString(),
      dueDate: dueDate.toISOString(),
      status: 'Pending',
      requestId: 'CCPA-' + Date.now(),
      source: 'Admin Portal'
    };
    
    await axios.post('https://nforgn9o9i.execute-api.ap-southeast-2.amazonaws.com/prod/api/ccpa/consumer-requests/create', payload, { headers });
    
    setSubmissionSuccess(true);
    showNotification('CCPA consumer request submitted successfully!', 'success');
    await logActivity(`Submitted CCPA consumer request: ${payload.type}`, "success");
    logToAccessLogs?.({
        action:      'Consumer Request Created',
        severity:    'success',
        description: `Created CCPA ${newRequest.type} request for ${newRequest.consumerEmail}`,});
    
    // Refresh data to show new request
    setTimeout(() => {
      fetchRequests();
      resetSubmitForm();
    }, 2000);
    
  } catch (error) {
    console.error('Error submitting consumer request:', error);
    showNotification('Error submitting consumer request', 'danger');
  }
};

const resetSubmitForm = () => {
  setNewRequest({
    type: 'Right to Know',
    consumerEmail: '',
    firstName: '',
    lastName: '',
    details: '',
    verificationMethod: 'Email',
    phoneNumber: '',
    address: ''
  });
  setShowSubmitModal(false);
  setSubmissionSuccess(false);
};


  const getStatusVariant = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'In Progress': return 'primary';
      case 'Pending': return 'warning';
      case 'Rejected': return 'danger';
      default: return 'secondary';
    }
  };

  const getDaysRemaining = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <>
      <Alert variant="info">
        <strong>CCPA Consumer Rights:</strong> California consumers have the right to know, delete, opt-out of sale, 
        and non-discrimination. Response time: 45 days (extendable to 90 days)[17][26].
      </Alert>
      {/* Add this before the Stats Cards Row */}
<Row className="mb-3">
  <Col>
    <Button variant="primary" onClick={() => setShowSubmitModal(true)}>
      <FaPlus /> Submit Consumer Request
    </Button>
  </Col>
</Row>


      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4>{stats.total}</h4>
              <small>Total Requests</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-warning">{stats.pending}</h4>
              <small>Pending</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-success">{stats.completed}</h4>
              <small>Completed</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center">
            <Card.Body>
              <h4 className="text-danger">{stats.overdue}</h4>
              <small>Overdue</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Consumer Requests Table */}
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Request ID</th>
            <th>Type</th>
            <th>Consumer</th>
            <th>Request Date</th>
            <th>Due Date (45 days)</th>
            <th>Status</th>
            <th>Days Remaining</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {requests.length === 0 ? (
            <tr><td colSpan={8} className="text-center">No CCPA consumer requests found.</td></tr>
          ) : (
            requests.map((r) => (
              <tr key={r.requestId}>
                <td>#{r.requestId}</td>
                <td>
                  <Badge bg={
                    r.type === 'Right to Know' ? 'info' :
                    r.type === 'Right to Delete' ? 'danger' :
                    r.type === 'Opt-Out of Sale' ? 'warning' :
                    r.type === 'Right to Correct' ? 'primary' : 'secondary'
                  }>
                    {r.type}
                  </Badge>
                </td>
                <td>{r.consumerEmail}</td>
                <td>{new Date(r.requestDate).toLocaleDateString()}</td>
                <td>{new Date(r.dueDate).toLocaleDateString()}</td>
                <td>
                  <Badge bg={getStatusVariant(r.status)}>{r.status}</Badge>
                </td>
                <td>
                  {r.status !== 'Completed' && (
                    <span className={getDaysRemaining(r.dueDate) < 0 ? 'text-danger fw-bold' : ''}>
                      {getDaysRemaining(r.dueDate)} days
                    </span>
                  )}
                </td>
                <td>
                  <Button size="sm" variant="outline-primary" className="me-1" 
                          onClick={() => { setViewingRequest(r); setShowModal(true); }}>
                    <FaEye />
                  </Button>
                  {r.status === 'Completed' && (
                    <Button size="sm" variant="outline-success" className="me-1"
                            onClick={() => exportResponse(r.requestId)}>
                      <FaDownload />
                    </Button>
                  )}
                  {r.status !== 'Completed' && (
                    <Button size="sm" variant="outline-warning"
                            onClick={() => handleStatusUpdate(r.requestId, 'In Progress')}>
                      Process
                    </Button>
                  )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Request Details Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>CCPA Consumer Request - #{viewingRequest?.requestId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewingRequest && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Request Type</Form.Label>
                    <Form.Control type="text" value={viewingRequest.type} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Current Status</Form.Label>
                    <Form.Select 
                      value={viewingRequest.status}
                      onChange={(e) => handleStatusUpdate(viewingRequest.requestId, e.target.value)}
                    >
                      <option value="Pending">Pending</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                      <option value="Rejected">Rejected</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Consumer Email</Form.Label>
                <Form.Control type="email" value={viewingRequest.consumerEmail} readOnly />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Request Details</Form.Label>
                <Form.Control 
                  as="textarea" 
                  rows={3} 
                  value={viewingRequest.details || 'No additional details provided.'} 
                  readOnly 
                />
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Request Date</Form.Label>
                    <Form.Control type="text" value={new Date(viewingRequest.requestDate).toLocaleDateString()} readOnly />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Due Date (45 days)</Form.Label>
                    <Form.Control type="text" value={new Date(viewingRequest.dueDate).toLocaleDateString()} readOnly />
                  </Form.Group>
                </Col>
              </Row>
              {viewingRequest.verificationMethod && (
                <Form.Group className="mb-3">
                  <Form.Label>Identity Verification Method</Form.Label>
                  <Form.Control type="text" value={viewingRequest.verificationMethod} readOnly />
                </Form.Group>
              )}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
          {viewingRequest?.status === 'Completed' && (
            <Button variant="primary" onClick={() => exportResponse(viewingRequest.requestId)}>
              <FaDownload /> Download Response
            </Button>
          )}
        </Modal.Footer>
      </Modal>
      {/* Add this new modal after the existing request details modal */}
<Modal show={showSubmitModal} onHide={resetSubmitForm} size="lg">
  <Modal.Header closeButton>
    <Modal.Title>Submit CCPA Consumer Request</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {submissionSuccess ? (
      <Alert variant="success">
        <h5>Request Submitted Successfully!</h5>
        <p>Your CCPA consumer request has been submitted and will be processed within 45 days.</p>
        <p><strong>Request Type:</strong> {newRequest.type}</p>
        <p><strong>Consumer Email:</strong> {newRequest.consumerEmail}</p>
        <p><strong>Due Date:</strong> {new Date(Date.now() + (45 * 24 * 60 * 60 * 1000)).toLocaleDateString()}</p>
      </Alert>
    ) : (
      <>
        <Alert variant="info">
          <strong>CCPA Consumer Rights:</strong> California consumers have the right to know what personal 
          information is collected, delete personal information, opt-out of sale, and non-discrimination.
        </Alert>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Request Type *</Form.Label>
            <Form.Select
              value={newRequest.type}
              onChange={(e) => setNewRequest({...newRequest, type: e.target.value})}
            >
              {REQUEST_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              {newRequest.type === 'Right to Know' && 'Request categories and specific pieces of personal information collected'}
              {newRequest.type === 'Right to Delete' && 'Request deletion of personal information'}
              {newRequest.type === 'Opt-Out of Sale' && 'Opt-out of sale or sharing of personal information'}
              {newRequest.type === 'Right to Correct' && 'Request correction of inaccurate personal information'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Consumer Email Address *</Form.Label>
            <Form.Control
              type="email"
              required
              value={newRequest.consumerEmail}
              onChange={(e) => setNewRequest({...newRequest, consumerEmail: e.target.value})}
              placeholder="Enter consumer's email address"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First Name *</Form.Label>
                <Form.Control
                  type="text"
                  required
                  value={newRequest.firstName}
                  onChange={(e) => setNewRequest({...newRequest, firstName: e.target.value})}
                  placeholder="Enter first name"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last Name *</Form.Label>
                <Form.Control
                  type="text"
                  required
                  value={newRequest.lastName}
                  onChange={(e) => setNewRequest({...newRequest, lastName: e.target.value})}
                  placeholder="Enter last name"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Request Details</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newRequest.details}
              onChange={(e) => setNewRequest({...newRequest, details: e.target.value})}
              placeholder="Provide specific details about the request (optional)"
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Verification Method</Form.Label>
                <Form.Select
                  value={newRequest.verificationMethod}
                  onChange={(e) => setNewRequest({...newRequest, verificationMethod: e.target.value})}
                >
                  {VERIFICATION_METHODS.map(method => (
                    <option key={method} value={method}>{method}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="tel"
                  value={newRequest.phoneNumber}
                  onChange={(e) => setNewRequest({...newRequest, phoneNumber: e.target.value})}
                  placeholder="Optional - for verification"
                />
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Address</Form.Label>
            <Form.Control
              type="text"
              value={newRequest.address}
              onChange={(e) => setNewRequest({...newRequest, address: e.target.value})}
              placeholder="Optional - for verification purposes"
            />
          </Form.Group>

          <Alert variant="warning" className="small">
            <strong>Processing Timeline:</strong> This request will be processed within 45 days as required 
            by CCPA regulations. Complex requests may require an additional 45 days with proper notice.
          </Alert>
        </Form>
      </>
    )}
  </Modal.Body>
  <Modal.Footer>
    {submissionSuccess ? (
      <Button variant="primary" onClick={resetSubmitForm}>Close</Button>
    ) : (
      <>
        <Button variant="secondary" onClick={resetSubmitForm}>Cancel</Button>
        <Button 
          variant="primary" 
          onClick={handleSubmitRequest}
          disabled={!newRequest.consumerEmail || !newRequest.firstName || !newRequest.lastName}
        >
          Submit Request
        </Button>
      </>
    )}
  </Modal.Footer>
</Modal>

    </>
  );
};

export default CCPADsarManagementTab;


    